<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        class="searchInput"
        state="default"
        placeholder="Code (AKTO, Interne, Déca), nom du stagiaire, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <div class="searchWrapper--actions">
        <BasicTooltip label="Exporter les dossiers">
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="left"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            label="Créer un nouveau dossier"
            size="large"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="top"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            label="Créer un nouveau dossier"
            icon-name="folder-add"
            size="large"
            width="full"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
        <BasicTooltip
          label="Exporter les dossiers"
          position="left"
        >
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
      </div>
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Code (AKTO, Interne, Déca), nom du stagiaire..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="dispositifsSousDispositifs"
          label="Dispositif/Sous-dispositif"
          :items="dispositifSousDispositifItems"
          :itemId="(item) => Object.keys(item).join('!')"
          placeholder="Sélection"
          class="col-9 col-md-18 col-sm-36"
        />
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-6 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="periode"
          label="Période"
          :items="periodeItems"
          placeholder="Sélection"
          class="col-5 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="mandat"
          label="Mandat de gestion"
          :items="boolDropDownItems"
          placeholder="Sélection"
          class="col-4 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="subrogation"
          label="Subrogation"
          :items="boolDropDownItems"
          placeholder="Sélection"
          class="col-4 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-if="hasInterimaire"
          v-model="typePublic"
          :items="typePublicItems"
          label="Type de public"
          placeholder="Sélection"
          class="col-4 col-md-18 col-sm-36"
        />
        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!searchIsFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              type="reverse"
              iconName="filter-remove"
              @click="removeFilters"
              :disabled="!searchIsFiltered"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="paginationNumberOfPages"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
      <div class="informationDetailWrapper">
        <div class="informationElem">
          <BasicIcon
            name="document-text"
            active
          />
          <BasicText size="body-m">Mandat de gestion</BasicText>
        </div>
        <div class="informationElem">
          <BasicIcon
            name="wallet"
            :active="false"
          />
          <BasicText size="body-m">Subrogation de paiement</BasicText>
        </div>
        <div class="informationElem">
          <BasicIcon
            name="danger"
            :active="false"
            class="danger"
          />
          <BasicText size="body-m">Alerte</BasicText>
        </div>
      </div>
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <DossierCardMobile
          v-for="(item, index) in store.pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <DossierHeader v-model="order" />
        <DossierCardDesktop
          v-for="(item, index) in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Dossiers"
        headerText="Dossiers"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RechercheDossierQueryDTO } from '@/features/api/extranet/dto/Dossier/Recherche'
  import { useAppStateStore } from '@/features/application'
  import { usePreferencesUtilisateur } from '@/features/application/preferences'
  import { PREFERENCES_UTILISATEUR_APP, type PreferenceGlobale } from '@/features/application/types'
  import { DispositifSousDipositifFormation } from '@/features/domaine/types/opco/Demande/shared/DispositifSousDispositif'
  import { StatutLibelle } from '@/features/domaine/types/opco/Dossier'
  import { TypePublicLibelle } from '@/features/domaine/types/opco/shared'
  import { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
  import { collapseDispositifs, expandDispositifs } from '@/features/domaine/types/opco/shared/dispositifUtils'
  import { PeriodeLibelle } from '@/features/domaine/types/opco/shared/Periode'
  import { useImmerFieldPatcher } from '@/features/interface/composables'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import { useRattachementsStore } from '@/features/interface/profil-utilisateur/sections/rattachements/useRattachementsStore'
  import WrapPermissions from '@/features/interface/shared/wrap-permissions/WrapPermissions.vue'
  import { isNotNullUndefinedOrEmptyString, isObjectEquals } from '@/features/shared/tools'
  import { formatSearchQuery, parseSearchQuery } from '@/features/shared/tools/parse'
  import { navigateTo, type NavigateToQueryDossierFormation, type WithNavigateToLocation } from '@/router/navigateTo'
  import { storeToRefs } from 'pinia'
  import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { useDialog } from '../../dialog'
  import { boolDropDownItems, objectToDropdownItems, objectToDropdownMultipleItems } from '../../shared/tools'
  import { pageSizeItems } from '../dropdown-items'
  import type { PagedRequest } from '../storeRechercheBuilder'
  import { DossierCardDesktop, DossierCardMobile, DossierHeader } from './components'
  import { useRechercheDossierActions } from './useRechercheDossierActions'
  import { useRechercheDossierStore } from './useRechercheDossierStore'

  const props = defineProps<WithNavigateToLocation<NavigateToQueryDossierFormation>>()

  const rattachementsStore = useRattachementsStore()
  const { hasInterimaire } = storeToRefs(rattachementsStore)

  const store = useRechercheDossierStore()
  onBeforeUnmount(store.$reset)
  const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)

  const appState = useAppStateStore()

  const { addFavori, addDemande } = useRechercheDossierActions()

  const { setupFieldWithKey, setupFieldWithOptions, setupCustomUpdater } = useImmerFieldPatcher<
    PagedRequest<RechercheDossierQueryDTO>
  >({
    getModel: () => store.pageRequest!,
    setModel(newModel) {
      const queryChanged = !isObjectEquals(store.pageRequest?.query, newModel.query)
      if (queryChanged) {
        newModel = {
          ...newModel,
          pagination: {
            pageNumber: 1,
            pageSize: newModel.pagination.pageSize,
          },
        }
      }
      navigateTo({
        ...props.navigateToLocation,
        ...newModel,
      })
    },
    debounceDelay: 1000,
  })

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })

  const showFilters = ref(false)

  const statutsItems = objectToDropdownItems(StatutLibelle)
  const typePublicItems = objectToDropdownItems(TypePublicLibelle)

  const dispositifsSousDispositifs = setupFieldWithOptions<string[]>({
    getValue: () => {
      if (!store.pageRequest) return []
      return expandDispositifs(store.pageRequest.query.dispositifs ?? [])
    },
    setPatch: (patch, value) => {
      patch.query.dispositifs = collapseDispositifs(value ?? [])
    },
  })
  const statuts = setupFieldWithKey<string[]>('query.statuts')
  const subrogation = setupFieldWithKey<boolean | null | undefined>('query.avecSubrogation')
  const mandat = setupFieldWithKey<boolean | null | undefined>('query.estSousMandatGestion')
  const pageNumber = setupFieldWithKey<number>('pagination.pageNumber')
  const periodeItems = objectToDropdownItems(PeriodeLibelle)
  const periode = setupFieldWithKey<string | null | undefined>('query.periode')
  const typePublic = setupFieldWithKey<string | null | undefined>('query.typePublic')

  const exporter = async () => {
    await store.printData()
    useDialog().showDialog({
      type: 'Ok',
      title: 'Export des dossiers',
      message: 'Vos dossiers ont été exportés avec succés, vous allez les recevoir par mail',
      closable: true,
    })
  }

  const pageSize = setupFieldWithOptions<number | null | undefined>({
    getValue: (model) => model.pagination?.pageSize,
    setPatch: (patch, value) => {
      patch.pagination = {
        pageNumber: 1,
        pageSize: value!,
      }
      prefService.setPreferencesAsync({ pageSize: value! })
    },
  })

  const order = setupFieldWithOptions<string>({
    getValue: () => {
      return store.pageRequest?.orderBy?.at(0) ?? ''
    },
    setPatch: (patch, value) => {
      patch.orderBy = value ? [value] : []
      patch.pagination = {
        pageNumber: 1,
        pageSize: patch.pagination.pageSize,
      }
    },
  })

  const search = setupFieldWithOptions<string>({
    getValue: () => formatSearchQuery(store.pageRequest?.query.search ?? []),
    setPatch: (patch, value) => {
      if (!value) patch.query.search = []
      else patch.query.search = parseSearchQuery(value)
    },
    withDebounce: true,
  })

  const pageChange = (page: number) => {
    pageNumber.value = page
  }

  const searchIsFiltered = computed(
    () =>
      search.value.length ||
      dispositifsSousDispositifs.value.length ||
      statuts.value.length ||
      mandat.value != null ||
      subrogation.value != null ||
      periode.value != null ||
      typePublic.value != null,
  )

  const removeFilters = setupCustomUpdater((pageRequest) => {
    pageRequest.query = {
      search: undefined,
      estSousMandatGestion: undefined,
      avecSubrogation: undefined,
      dispositifs: undefined,
      periode: undefined,
      typePublic: undefined,
    }
    showFilters.value = false
  })

  const paginationNumberOfPages = computed(() => {
    if (store?.pageData?.totalRecords) {
      return Math.ceil(store?.pageData?.totalRecords / store.pageRequest!.pagination!.pageSize!)
    }
    return 0
  })

  const DispositifSousDipositifFormationProfil = computed(() => {
    const { FPE, ...dispositifSansFPE } = DispositifSousDipositifFormation

    return appState.autoriseLecture('FPE') ? DispositifSousDipositifFormation : dispositifSansFPE
  })

  const dispositifSousDispositifItems = objectToDropdownMultipleItems(DispositifSousDipositifFormationProfil.value)

  onBeforeMount(() => {
    if (
      dispositifsSousDispositifs.value.length ||
      statuts.value.length ||
      mandat.value != null ||
      subrogation.value != null ||
      isNotNullUndefinedOrEmptyString(periode.value) ||
      typePublic.value != null
    ) {
      showFilters.value = true
    }
  })
  /*
  onBeforeRouteUpdate(async (to) => {
    return await loadStoreFromLocationQuery(to.query)
  })
   */
</script>

<style scoped>
  @import '../index.less';
</style>
